import React, { useEffect, useMemo, useState } from 'react'
import { Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box, Button } from '@mui/material'
import { API_BASE, authHeaders, sortRows } from './api'
import axios from 'axios'
import { useLedger } from './LedgerContext'

const toSkDT = (iso) => iso ? new Date(iso).toLocaleString('sk-SK') : '-'

export default function WalletsPanel({ wallets, selectedWalletUserId, setSelectedWalletUserId, filters }) {
  const { sortWallet, setSortWallet } = useLedger()
  const [pageWallet, setPageWallet] = useState(1)
  const [selectedWalletRows, setSelectedWalletRows] = useState([])

  const toggleSortWallet = (field) => setSortWallet(s => s.field === field ? { field, dir: (s.dir === 'asc' ? 'desc' : 'asc') } : { field, dir: 'asc' })
  const walletRowsSorted = useMemo(() => sortRows(selectedWalletRows, sortWallet), [selectedWalletRows, sortWallet])

  useEffect(() => { setPageWallet(1) }, [filters])
  useEffect(() => {
    if (!selectedWalletUserId) { setSelectedWalletRows([]); return }
    const qp = new URLSearchParams()
    qp.set('account', 'USER_WALLET')
    qp.set('userId', String(selectedWalletUserId))
    if (filters.dateFrom) qp.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) qp.set('dateTo', filters.dateTo)
    if (filters.source) qp.set('source', filters.source)
    const limit = Number(filters.limit) || 500
    qp.set('limit', String(limit))
    const offset = (Math.max(1, pageWallet) - 1) * limit
    qp.set('offset', String(offset))
    axios.get(`${API_BASE}/api/ledger?${qp.toString()}`, { headers: authHeaders() })
      .then(res => setSelectedWalletRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSelectedWalletRows([]))
  }, [selectedWalletUserId, filters.dateFrom, filters.dateTo, filters.source, filters.limit, pageWallet])

  const visibleWallets = useMemo(() => {
    if (selectedWalletUserId == null) return wallets
    return wallets.filter(w => String(w.userId) === String(selectedWalletUserId))
  }, [wallets, selectedWalletUserId])

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Peňaženky používateľov</Typography>
        {selectedWalletUserId != null && (
          <Button variant="text" onClick={() => { setSelectedWalletUserId(null); setPageWallet(1) }}>Zobraziť všetky</Button>
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Currency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleWallets.length === 0 && <TableRow><TableCell colSpan={3}>Žiadne dáta</TableCell></TableRow>}
            {visibleWallets.map(w => (
              <TableRow key={`${w.userId}-${w.currency}`} hover onClick={() => { setSelectedWalletUserId(w.userId); setPageWallet(1) }} sx={{ cursor: 'pointer' }}>
                <TableCell>{w.userId}</TableCell>
                <TableCell align="right">{Number(w.balance || 0)}</TableCell>
                <TableCell>{w.currency}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedWalletUserId && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1">Pohyby peňaženky – userId: {selectedWalletUserId}</Typography>
            <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
              <Button variant="outlined" disabled={pageWallet <= 1} onClick={() => setPageWallet(p => Math.max(1, p - 1))}>Predchádzajúca</Button>
              <Button variant="outlined" disabled={walletRowsSorted.length < (Number(filters.limit) || 500)} onClick={() => setPageWallet(p => p + 1)}>Ďalšia</Button>
              <Box sx={{ alignSelf: 'center', ml: 1 }}>Strana: {pageWallet}</Box>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => toggleSortWallet('txn_id')} sx={{ cursor: 'pointer' }}>Txn ID</TableCell>
                  <TableCell onClick={() => toggleSortWallet('side')} sx={{ cursor: 'pointer' }}>Side</TableCell>
                  <TableCell onClick={() => toggleSortWallet('account')} sx={{ cursor: 'pointer' }}>Account</TableCell>
                  <TableCell onClick={() => toggleSortWallet('userId')} sx={{ cursor: 'pointer' }}>User ID</TableCell>
                  <TableCell onClick={() => toggleSortWallet('amount')} sx={{ cursor: 'pointer' }} align="right">Amount</TableCell>
                  <TableCell onClick={() => toggleSortWallet('currency')} sx={{ cursor: 'pointer' }}>Currency</TableCell>
                  <TableCell onClick={() => toggleSortWallet('source')} sx={{ cursor: 'pointer' }}>Source</TableCell>
                  <TableCell onClick={() => toggleSortWallet('description')} sx={{ cursor: 'pointer' }}>Description</TableCell>
                  <TableCell onClick={() => toggleSortWallet('createdAt')} sx={{ cursor: 'pointer' }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletRowsSorted.length === 0 && <TableRow><TableCell colSpan={9}>Žiadne dáta</TableCell></TableRow>}
                {walletRowsSorted.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.txn_id}</TableCell>
                    <TableCell>{r.side}</TableCell>
                    <TableCell>{r.account}</TableCell>
                    <TableCell>{r.userId ?? '-'}</TableCell>
                    <TableCell align="right">{Number(r.amount || 0)}</TableCell>
                    <TableCell>{r.currency || 'EUR'}</TableCell>
                    <TableCell>{r.source}</TableCell>
                    <TableCell>{r.description || '-'}</TableCell>
                    <TableCell>{toSkDT(r.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  )
}
